import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

import {
  formatIdentityDiffSummary,
  getIdentityKeyedArrayDiff,
  isIdentityKeyedArrayUnchanged,
} from './arrayIdentityDiff';
import { REORDERED_SUMMARY, UNCHANGED_SUMMARY } from './constants';
import { formatSimpleValue, getChangeStatus, isFacilityItem, isFeaturedFacility, isLongTextChange } from './formatters';
import type { ListItemStatus } from './types';
import { itemIdentity } from './utils';

export function isFacilityArray(items: Array<unknown>): boolean {
  return items.length > 0 && items.every(isFacilityItem);
}

function getFacilityDisplayOrder(value: unknown): number {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (typeof record.displayOrder === 'number') {
      return record.displayOrder;
    }
  }
  return Number.MAX_SAFE_INTEGER;
}

export function sortFacilities(items: Array<unknown>): Array<unknown> {
  if (!isFacilityArray(items)) {
    return items;
  }

  return sortBy(items, [(item) => getFacilityDisplayOrder(item), (item) => itemIdentity(item)]);
}

function getFacilityContentWithoutFeatured(item: Record<string, unknown>): Record<string, unknown> {
  const { featured: _featured, ...content } = item;
  return content;
}

function getFacilityComparableContent(item: Record<string, unknown>): Record<string, unknown> {
  const { displayOrder: _displayOrder, ...content } = item;
  return content;
}

function isFacilityDisplayOrderOnlyChange(item: unknown, previousItem: unknown): boolean {
  if (!isFacilityItem(item) || !isFacilityItem(previousItem)) {
    return false;
  }
  if (isEqual(item, previousItem)) {
    return false;
  }

  return isEqual(getFacilityComparableContent(item), getFacilityComparableContent(previousItem));
}

function getFacilityFeaturedOnlyChange(
  item: unknown,
  compareItem: unknown,
): 'featured-added' | 'featured-removed' | undefined {
  if (!isFacilityItem(item) || !isFacilityItem(compareItem)) {
    return undefined;
  }
  if (isEqual(item, compareItem)) {
    return undefined;
  }
  if (!isEqual(getFacilityContentWithoutFeatured(item), getFacilityContentWithoutFeatured(compareItem))) {
    return undefined;
  }

  if (!isFeaturedFacility(compareItem) && isFeaturedFacility(item)) {
    return 'featured-added';
  }
  if (isFeaturedFacility(compareItem) && !isFeaturedFacility(item)) {
    return 'featured-removed';
  }

  return undefined;
}

function getFacilityChangeCounts(prev: Array<unknown>, next: Array<unknown>) {
  const { added, removed, previousByIdentity } = getIdentityKeyedArrayDiff(prev, next);
  let changed = 0;
  let newlyFeatured = 0;
  let unfeatured = 0;
  let reordered = 0;

  for (const item of next) {
    const previousItem = previousByIdentity[itemIdentity(item)];
    if (!previousItem) {
      continue;
    }

    const featuredChange = getFacilityFeaturedOnlyChange(item, previousItem);
    if (featuredChange === 'featured-added') {
      newlyFeatured++;
      continue;
    }

    if (featuredChange === 'featured-removed') {
      unfeatured++;
      continue;
    }

    if (isFacilityDisplayOrderOnlyChange(item, previousItem)) {
      reordered++;
      continue;
    }

    if (!isEqual(previousItem, item)) {
      changed++;
    }
  }

  return { added, removed, changed, newlyFeatured, unfeatured, reordered };
}

function hasNoFacilityChanges(counts: ReturnType<typeof getFacilityChangeCounts>): boolean {
  return (
    counts.added.length === 0 &&
    counts.removed.length === 0 &&
    counts.changed === 0 &&
    counts.newlyFeatured === 0 &&
    counts.unfeatured === 0 &&
    counts.reordered === 0
  );
}

function isFacilityArrayReorderedOnly(prev: Array<unknown>, next: Array<unknown>): boolean {
  if (isEqual(prev, next)) {
    return false;
  }

  const { added, removed, changed, newlyFeatured, unfeatured, reordered } = getFacilityChangeCounts(prev, next);

  return (
    added.length === 0 &&
    removed.length === 0 &&
    changed === 0 &&
    newlyFeatured === 0 &&
    unfeatured === 0 &&
    reordered > 0
  );
}

export function isUnchangedValue(prev: unknown, next: unknown): boolean {
  if (isEqual(prev, next)) {
    return true;
  }
  if (!Array.isArray(prev) || !Array.isArray(next)) {
    return false;
  }

  if (isFacilityArray(prev) || isFacilityArray(next)) {
    return hasNoFacilityChanges(getFacilityChangeCounts(prev, next));
  }

  return isIdentityKeyedArrayUnchanged(getIdentityKeyedArrayDiff(prev, next));
}

export function isReorderedOnly(prev: unknown, next: unknown): boolean {
  if (isUnchangedValue(prev, next)) {
    return false;
  }
  if (!Array.isArray(prev) || !Array.isArray(next)) {
    return false;
  }

  if (isFacilityArray(prev) || isFacilityArray(next)) {
    return isFacilityArrayReorderedOnly(prev, next);
  }

  return !isEqual(prev, next) && isUnchangedValue(prev, next);
}

function getFacilityArraySummary(prev: Array<unknown>, next: Array<unknown>): string {
  const { added, removed, changed, newlyFeatured, unfeatured, reordered } = getFacilityChangeCounts(prev, next);

  const summaryParts = [
    added.length > 0 ? `${added.length} added` : undefined,
    removed.length > 0 ? `${removed.length} removed` : undefined,
    changed > 0 ? `${changed} changed` : undefined,
    reordered > 0 ? `${reordered} reordered` : undefined,
    newlyFeatured > 0 ? `${newlyFeatured} featured` : undefined,
    unfeatured > 0 ? `${unfeatured} unfeatured` : undefined,
  ].filter(Boolean);

  if (summaryParts.length > 0) {
    return summaryParts.join(', ');
  }
  if (isUnchangedValue(prev, next)) {
    return UNCHANGED_SUMMARY;
  }
  if (isFacilityArrayReorderedOnly(prev, next)) {
    return REORDERED_SUMMARY;
  }

  return `${prev.length} -> ${next.length} items`;
}

function getArraySummary(prev: Array<unknown>, next: Array<unknown>): string {
  if (prev.length === 0 && next.length === 0) {
    return 'No items';
  }

  if (isFacilityArray(prev) || isFacilityArray(next)) {
    return getFacilityArraySummary(prev, next);
  }

  const diff = getIdentityKeyedArrayDiff(prev, next);
  const summary = formatIdentityDiffSummary(diff);

  return (
    summary ||
    (isUnchangedValue(prev, next)
      ? UNCHANGED_SUMMARY
      : isReorderedOnly(prev, next)
        ? REORDERED_SUMMARY
        : `${prev.length} -> ${next.length} items`)
  );
}

export function getSummary(key: string, prev: unknown, next: unknown): string {
  if (key === 'logo_id_cloudinary_external') {
    return 'Logo changed';
  }
  if (isLongTextChange(prev, next)) {
    return getChangeStatus(prev, next);
  }

  if (Array.isArray(prev) || Array.isArray(next)) {
    return getArraySummary(Array.isArray(prev) ? prev : [], Array.isArray(next) ? next : []);
  }

  return `${formatSimpleValue(prev)} -> ${formatSimpleValue(next)}`;
}

export function getListItemStatus(
  item: unknown,
  compareByIdentity: Record<string, unknown> | undefined,
  panel: 'before' | 'after' | undefined,
): ListItemStatus | undefined {
  if (!compareByIdentity || !panel) {
    return undefined;
  }

  const compareItem = compareByIdentity[itemIdentity(item)];

  if (panel === 'after') {
    if (!compareItem) {
      return 'added';
    }

    const featuredChange = getFacilityFeaturedOnlyChange(item, compareItem);
    if (featuredChange) {
      return featuredChange;
    }

    if (isFacilityDisplayOrderOnlyChange(item, compareItem)) {
      return 'reordered';
    }

    if (!isEqual(compareItem, item)) {
      return 'changed';
    }
    return undefined;
  }

  if (!compareItem) {
    return 'removed';
  }

  const featuredChange = getFacilityFeaturedOnlyChange(compareItem, item);
  if (featuredChange === 'featured-added') {
    return 'featured-added';
  }
  if (featuredChange === 'featured-removed') {
    return 'featured-removed';
  }

  if (isFacilityDisplayOrderOnlyChange(compareItem, item)) {
    return 'reordered';
  }

  if (!isEqual(compareItem, item)) {
    return 'changed';
  }
  return undefined;
}
